/**
 * Project-wide domain-realness validation.
 *
 * SINGLE SOURCE OF TRUTH for "is this a real, publicly-registrable
 * domain" across every Trustico® SSL tool : CSR Generator, CAA
 * Generator, CAA Checker, Domain Control Validation (DCV) Checker,
 * the Hosted SSL Certificate Issuance Tool, and the public
 * POST /api/domain/validate endpoint. Backed by the Public Suffix
 * List (PSL) via the `tldts` library - the same library the sibling
 * projects tracking-01 (lib/auth-domain.ts) and middleware-01
 * (src/handlers/api/domain.js) use, so the whole Trustico® stack
 * judges domain realness one consistent way.
 *
 * 2026-06-01 : replaces the former hand-rolled reserved-suffix
 * blocklists that were duplicated across the tools. Those lists
 * only denied a handful of RFC 6761 / 7686 special-use names
 * (.local, .test, .invalid, .onion, ...) and did NOT verify the
 * Top-Level Domain (TLD) was real - a fake TLD such as
 * "example.madeuptld" sailed straight through them. tldts inverts
 * the logic : instead of denying a short list, it AFFIRMATIVELY
 * requires a real ICANN-registry TLD.
 *
 * Originally lived at src/issuance/domainValidation.js ; promoted
 * to src/utils/ when the realness gate was adopted project-wide, and
 * moved to src/shared/utils/ in the 2026-07-04 directory restructure
 * (docs/TOOLS_CONSISTENCY_AUDIT.md, "Directory structure") because it
 * is imported by BOTH the browser tier and the Express tier.
 */

import { parse as parseDomain } from "tldts";

/**
 * True when `host` is a real, publicly-registrable domain whose
 * Top-Level Domain (TLD) sits in the ICANN section of the Public
 * Suffix List (PSL), as determined by tldts.
 *
 * Returns FALSE for :
 *   - fake / non-existent TLDs ............ example.madeuptld, example.zzz
 *   - RFC 6761 special-use names .......... foo.local, foo.test, foo.invalid
 *   - bare single labels .................. localhost
 *   - Internet Protocol (IP) literals ..... 192.168.1.1
 *
 * The caller is expected to have already stripped any "*." wildcard
 * prefix (tldts treats "*" as a label and would mis-parse it). This
 * function is the realness gate only - basic syntax / length checks
 * are the caller's job where they apply.
 *
 * EXACT-HOSTNAME GATE (2026-07-04 CI review 4881747796 finding #1) :
 * tldts.parse defaults to mixedInputs:true, so it EXTRACTS a hostname
 * out of URL-shaped junk - "example.com/path", "example.com:6379",
 * "https://evil.com@example.com/", "example.com." - and reported
 * isIcann:true for all of them, making this helper's caller contract
 * hazardous : a caller that trusted the boolean could feed the raw
 * junk onward into a DNS lookup or a Subject Alternative Name (SAN).
 * The fix, applied at the SOURCE for every consumer : after tldts
 * parses, the input (lowercased - tldts case-folds its hostname)
 * must EXACTLY EQUAL the parsed hostname. Any mismatch (a path, a
 * port, a scheme, userinfo, trailing dots, embedded whitespace)
 * means the input was not a bare hostname → false. Bare valid
 * hostnames are UNCHANGED : for them the parsed hostname IS the
 * lowercased input, so the equality holds (pinned by the
 * "URL-shaped junk" describe in
 * src/__tests__/utils/domainValidation.test.js).
 *
 * NOTE on .onion (RFC 7686 Tor hidden services) : tldts reports it
 * as an ICANN public suffix (isIcann === true), so it PASSES this
 * check. A public Certificate Authority cannot issue a standard SSL
 * Certificate for .onion, but the relevant downstream gate (the
 * Automatic Certificate Management Environment (ACME) challenge for
 * issuance, a Domain Name System (DNS) lookup for the CAA / DCV
 * tools) rejects it. We deliberately do NOT re-introduce a
 * special-case blocklist : "let the downstream gate decide" keeps
 * this a single honest PSL check. Operator-confirmed 2026-06-01.
 *
 * @param {string} host - a hostname with the wildcard prefix already
 *   stripped (e.g. "example.com", not "*.example.com").
 * @returns {boolean}
 */
export function isRegistrablePublicDomain(host) {
  if (typeof host !== "string" || host.length === 0) return false;
  // Lowercase BEFORE the equality compare : tldts case-folds the
  // hostname it extracts, and mixed-case bare hostnames
  // ("Example.COM") were always accepted here, so the exact-hostname
  // gate must not change that (2026-07-04 CI review 4881747796
  // finding #1 behaviour pin).
  const candidate = host.toLowerCase();
  const parsed = parseDomain(candidate);
  // Exact-hostname gate (finding #1, see the JSDoc above) : reject
  // any input tldts had to EXTRACT a hostname from (URL-shaped junk)
  // rather than parse verbatim. parsed.hostname is null for input
  // tldts could not extract a hostname from at all - null !==
  // candidate, so that case falls through to false here too.
  if (parsed.hostname !== candidate) return false;
  // tldts.parse never throws on a string today ; isIcann === true
  // implies parsed.domain is non-null AND the public suffix is a
  // real ICANN-registry TLD. We compare strictly to `true` because
  // isIcann can be null (e.g. for IP literals).
  return parsed.isIcann === true;
}

/**
 * Richer description of a domain input, used by the public
 * POST /api/domain/validate endpoint so external callers get the
 * parsed structure, not just a boolean. Accepts an optional "*."
 * wildcard prefix and reports it separately.
 *
 * @param {string} input - the raw domain the caller submitted.
 * @returns {{
 *   input: string,
 *   normalized: string,
 *   isWildcard: boolean,
 *   baseDomain: string|null,
 *   publicSuffix: string|null,
 *   isValid: boolean
 * }}
 *   - input        : the trimmed raw value, echoed back
 *   - normalized   : the tldts-parsed hostname when the input was a
 *                    bare (possibly "*."-wildcarded) hostname ; for
 *                    input that FAILS the exact-hostname gate below,
 *                    the lower-cased wildcard-stripped input is
 *                    echoed alongside isValid:false and MUST NOT be
 *                    consumed as a hostname
 *   - isWildcard   : whether the input carried a "*." prefix
 *   - baseDomain   : the registrable apex (eTLD+1), e.g. "example.com"
 *   - publicSuffix : the public suffix, e.g. "com" or "co.uk"
 *   - isValid      : true only when the input is EXACTLY a hostname
 *                    (no URL parts - see the exact-hostname gate on
 *                    isRegistrablePublicDomain) AND its TLD is a real
 *                    ICANN suffix
 */
export function describeDomain(input) {
  const raw = typeof input === "string" ? input.trim() : "";
  const isWildcard = raw.startsWith("*.");
  // Lower-case + strip the wildcard prefix before parsing : tldts
  // treats "*" as a label and would otherwise mis-read the apex.
  const candidate = (isWildcard ? raw.slice(2) : raw).toLowerCase();
  const parsed = parseDomain(candidate);
  // Exact-hostname gate (2026-07-04 CI review 4881747796 finding #1,
  // pattern-swept with isRegistrablePublicDomain above) : tldts's
  // default mixedInputs:true EXTRACTS a hostname out of URL-shaped
  // junk ("example.com/path", "example.com:6379",
  // "https://evil.com@example.com/"), so isIcann alone reported
  // isValid:true while `normalized` still carried the raw junk - an
  // external consumer of POST /api/domain/validate trusting
  // { isValid:true, normalized } could feed garbage into DNS or a
  // Subject Alternative Name (SAN). The input must EXACTLY EQUAL the
  // parsed hostname or the verdict is false.
  const matchesParsedHostname =
    typeof parsed.hostname === "string" && parsed.hostname === candidate;
  return {
    input: raw,
    // Derived from the PARSED hostname whenever the gate holds - never
    // from raw string surgery (finding #1). On gate failure the
    // lower-cased candidate is echoed purely so the caller can see
    // what was judged ; isValid:false is the contract that it must
    // not be used as a hostname.
    normalized: matchesParsedHostname ? parsed.hostname : candidate,
    isWildcard,
    baseDomain: parsed.domain || null,
    publicSuffix: parsed.publicSuffix || null,
    isValid: parsed.isIcann === true && matchesParsedHostname,
  };
}
